import React, { useMemo } from "react";
import Badge from "../../../components/ui-components/Badge";
import { CapabilityPickerList } from "../capabilityPicker";
import { SENSITIVE_CAP } from "./constants";
import { WizardCard } from "./WizardUI";

// Computes the live diff between the template baseline and the currently
// selected capability set. Exported so the wizard's tests (and the shell
// component, for the sensitive-added governance default) can exercise it
// without re-deriving the same three arrays.
export function diffCapabilities(baselineCaps, selectedCaps) {
  const baseSet = new Set(baselineCaps);
  const curSet = new Set(selectedCaps);
  const unchanged = baselineCaps.filter((c) => curSet.has(c));
  const removed = baselineCaps.filter((c) => !curSet.has(c));
  const added = selectedCaps.filter((c) => !baseSet.has(c));
  return { unchanged, removed, added, sensitiveAdded: added.includes(SENSITIVE_CAP) };
}

// Step 3 (spec §3.3): shows the template baseline vs the proposed set, with
// a live diff summary and per-capability "Added" flags (sensitive ones
// called out specifically). Toggling recomputes on every render — no extra
// memoization beyond the diff itself, since selectedCaps already lives in
// the wizard shell's state.
export default function StepReviewCapabilities({ capabilityCatalog, baselineCaps, selectedCaps, toggleCap }) {
  const diff = useMemo(() => diffCapabilities(baselineCaps, selectedCaps), [baselineCaps, selectedCaps]);

  const renderAnnotation = (code) => {
    if (!diff.added.includes(code)) return null;
    return code === SENSITIVE_CAP ? (
      <Badge status="warning" label="Added · sensitive" size="sm" />
    ) : (
      <Badge status="info" label="Added" size="sm" />
    );
  };

  return (
    <WizardCard
      title="Review capabilities"
      subtitle={
        baselineCaps.length > 0
          ? "Compare against the template baseline and toggle any capability."
          : "Starting from scratch — select the capabilities this role grants."
      }
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="text-xs text-text-2" data-testid="capability-diff-summary">
          {diff.unchanged.length} unchanged · {diff.removed.length} removed · {diff.added.length} added
        </div>
        {diff.sensitiveAdded && (
          <Badge status="warning" label="Sensitive capability added" size="sm" />
        )}
      </div>

      <CapabilityPickerList
        capabilityCatalog={capabilityCatalog}
        selectedCaps={selectedCaps}
        onToggle={toggleCap}
        renderAnnotation={renderAnnotation}
      />
    </WizardCard>
  );
}
